// utils.js

// Format a full date (yyyy-mm-dd) to dd/mm/yyyy
export const formatDate = (dateString) => {
  if (!dateString) return ""; // Handle empty dates
  if (dateString.includes("-")) {
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  }
  return dateString;
};

// Format a month/year string (yyyy-mm) to mm/yyyy
export const formatMonthYear = (dateString) => {
  if (!dateString) return ""; // Handle empty dates
  if (dateString.includes("-")) {
    const [year, month] = dateString.split("-");
    return `${month}/${year}`;
  }
  return dateString;
};

export const sortByKey = (array, key) => {
  if (!Array.isArray(array)) return []; // Handle invalid inputs
  return array
    .slice()
    .sort((a, b) => (a[key] || "").localeCompare(b[key] || ""));
};

// Enhanced Validation Utils for Insurance Application
export const ValidationUtils = {
  // Email Validation
  validateEmail: (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return {
      isValid: emailRegex.test(email),
      message: emailRegex.test(email)
        ? ""
        : "Please enter a valid email address",
    };
  },

  // Phone Number Validation (Indian)
  validatePhone: (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return {
      isValid: phoneRegex.test(phone),
      message: phoneRegex.test(phone)
        ? ""
        : "Please enter a valid 10-digit mobile number",
    };
  },

  // PAN Number Validation (4th character must be 'P')
  validatePAN: (pan) => {
    const panRegex = /^[A-Z]{3}P[A-Z]{1}[0-9]{4}[A-Z]{1}$/;
    return {
      isValid: panRegex.test(pan),
      message: panRegex.test(pan)
        ? ""
        : "Please enter a valid PAN number (Format: ABCPA1234B - 4th character must be 'P')",
    };
  },

  // Company PAN Number Validation (4th character can be 'C', 'F', 'H', 'A', 'T', 'B', 'L', 'J', 'G')
  validateCompanyPAN: (pan) => {
    const companyPanRegex = /^[A-Z]{3}[CFHATBLJG]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/;
    return {
      isValid: companyPanRegex.test(pan),
      message: companyPanRegex.test(pan)
        ? ""
        : "Please enter a valid Company PAN number (Format: ABCCA1234B - 4th character must be C/F/H/A/T/B/L/J/G for entities)",
    };
  },

  // Aadhaar Validation
  validateAadhaar: (aadhaar) => {
    const aadhaarRegex = /^[2-9]{1}[0-9]{11}$/;
    return {
      isValid: aadhaarRegex.test(aadhaar),
      message: aadhaarRegex.test(aadhaar)
        ? ""
        : "Please enter a valid 12-digit Aadhaar number",
    };
  },

  // Name Validation (Only Letters and Spaces)
  validateName: (name) => {
    const nameRegex = /^[A-Za-z\s]+$/;
    return {
      isValid: nameRegex.test(name) && name.trim().length > 0,
      message:
        nameRegex.test(name) && name.trim().length > 0
          ? ""
          : "Please enter a valid name (letters and spaces only)",
    };
  },

  // Address Validation
  validateAddress: (address) => {
    const invalidChars = /[@$%^*~`()_+=?":{}|<>\\[\];''']/;
    return {
      isValid: !invalidChars.test(address) && address.length <= 60,
      message: invalidChars.test(address)
        ? "Address contains invalid characters"
        : address.length > 60
        ? "Address should not exceed 60 characters"
        : "",
    };
  },

  // Pincode Validation (Indian)
  validatePincode: (pincode) => {
    const pincodeRegex = /^[1-9][0-9]{5}$/;
    return {
      isValid: pincodeRegex.test(pincode),
      message: pincodeRegex.test(pincode)
        ? ""
        : "Please enter a valid 6-digit pincode",
    };
  },

  // Premium Amount Validation
  validatePremium: (amount) => {
    const isValid = !isNaN(amount) && parseFloat(amount) > 0;
    return {
      isValid: isValid,
      message: isValid ? "" : "Please enter a valid premium amount",
    };
  },

  // Generic Required Field Validation
  validateRequired: (value, fieldName) => {
    const isValid = value && value.toString().trim() !== "";
    return {
      isValid: isValid,
      message: isValid ? "" : `${fieldName} is required`,
    };
  },

  // Date of Birth Validation - For children (91 days to 25 years)
  validateDOBMinAge: (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    const diffTime = today - birthDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // Calculate age in years
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const actualAge =
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ? age - 1
        : age;

    // Check minimum 91 days and maximum 25 years
    const isMinAge = diffDays >= 91;
    const isMaxAge = actualAge <= 25;
    const isValid = isMinAge && isMaxAge;

    let message = "";
    if (!isMinAge) {
      message = "Date of birth must be at least 91 days ago";
    } else if (!isMaxAge) {
      message = "Child's age cannot exceed 25 years";
    }

    return {
      isValid: isValid,
      message: message,
      daysDifference: diffDays,
      calculatedAge: actualAge,
    };
  },

  // Date of Birth Validation - Minimum 18 years old (for adults)
  validateDOBAdult: (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Adjust age if birthday hasn't occurred this year
    const actualAge =
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ? age - 1
        : age;
    const isValid = actualAge >= 18 && actualAge <= 99;

    return {
      isValid: isValid,
      message: isValid ? "" : "Age must be between 18 and 99 years",
      calculatedAge: actualAge,
    };
  },

  // Text Length Validation
  validateLength: (value, maxLength, fieldName) => {
    const isValid = value.length <= maxLength;
    return {
      isValid: isValid,
      message: isValid
        ? ""
        : `${fieldName} should not exceed ${maxLength} characters`,
    };
  },

  // Number Validation
  validateNumber: (value, fieldName) => {
    const isValid = !isNaN(value) && value !== "";
    return {
      isValid: isValid,
      message: isValid ? "" : `Please enter a valid ${fieldName}`,
    };
  },

  // Generic Validation Function that works with your existing inputTypes
  validateByType: (value, inputType, fieldName, options = {}) => {
    if (!value || value === "") {
      if (options.required) {
        return ValidationUtils.validateRequired(value, fieldName);
      }
      return { isValid: true, message: "" };
    }

    switch (inputType.toLowerCase()) {
      case "mail":
      case "email":
        return ValidationUtils.validateEmail(value);

      case "telephone":
      case "phone":
        return ValidationUtils.validatePhone(value);

      case "pan":
        if (options.isCompanyPAN) {
          return ValidationUtils.validateCompanyPAN(value);
        }
        return ValidationUtils.validatePAN(value);

      case "text":
        if (options.isName) {
          return ValidationUtils.validateName(value);
        }
        if (options.isAddress) {
          return ValidationUtils.validateAddress(value);
        }
        if (options.isPAN) {
          return ValidationUtils.validatePAN(value);
        }
        if (options.isCompanyPAN) {
          return ValidationUtils.validateCompanyPAN(value);
        }
        if (options.maxLength) {
          return ValidationUtils.validateLength(
            value,
            options.maxLength,
            fieldName
          );
        }
        return { isValid: true, message: "" };

      case "number":
        return ValidationUtils.validateNumber(value, fieldName);

      case "date":
        if (options.isDOBMinAge) {
          return ValidationUtils.validateDOBMinAge(value);
        }
        if (options.isDOBAdult) {
          return ValidationUtils.validateDOBAdult(value);
        }
        return { isValid: true, message: "" };

      case "password":
        if (value.length < 6) {
          return {
            isValid: false,
            message: "Password must be at least 6 characters long",
          };
        }
        return { isValid: true, message: "" };

      default:
        return { isValid: true, message: "" };
    }
  },
};

const commonFunctions = {
  formatDate,
  formatMonthYear,
  sortByKey,
  ValidationUtils,
};

export default commonFunctions;
